import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import CobuTableAdvanced from '~/components/CobuTableAdvanced/CobuTableAdvanced';
import CobuSelect from '~/components/CobuSelect/CobuSelect';
import GenericModal from '~/components/GenericModal/GenericModal';
import { Tabs } from '~/components/Tabs';
import { Colors } from '~/enums/Colors';
import { Roles } from '~/enums/Roles';
import { SelectType } from '~/enums/SelectType';
import { withFullScreenLoader } from '~/hocs/withFullScreenLoader';
import { useFullScreenLoader } from '~/hooks/useFullScreenLoader';
import { RootState } from '~/redux/store';
import { ApiState } from '~/types/common';
import { SelectedOptionType } from '~/types/form';
import {
  ImageModerationAction,
  ImageModerationTab,
  ModerationTier,
  getImageScanHistory,
  getImagesToModerate,
  performModerationActionOnImage
} from '~/api/admin';
import classes from './ImageModerationContainer.module.css';
import ScanDetailModal from './ScanDetailModal';
import { getColumnsForImageModerationTable } from './tableColumns';
import {
  ALL_CATEGORIES,
  CATEGORY_LABELS,
  ImageScanHistoryResponse,
  ModeratedImage,
  QueueBuilding,
  Thresholds,
  categoryLabel
} from './types';

/**
 * The image moderation queues.
 *
 * "Auto-hidden" holds photos the model withheld at >=90% or could not scan;
 * UNHIDE releases them. "Needs review" holds the 50-89% band, which is still
 * visible to residents, so HIDE is the action there.
 */

const TABS: { label: string; value: ImageModerationTab }[] = [
  { label: 'AUTO-HIDDEN', value: 'hidden' },
  { label: 'NEEDS IMAGE REVIEW', value: 'review' }
];

const DEFAULT_THRESHOLDS: Thresholds = { autoHide: 90, questionable: 50 };

const CATEGORY_OPTIONS = [
  { label: 'All categories', value: ALL_CATEGORIES },
  ...Object.keys(CATEGORY_LABELS).map((key) => ({
    label: categoryLabel(key),
    value: key
  }))
];

// Same vocabulary as the comment tabs' Sort by / Order controls.
const SORT_OPTIONS = [
  { label: 'Chronology', value: 'chronology' },
  { label: 'Building', value: 'building' },
  { label: 'Confidence', value: 'confidence' },
  { label: 'Uploader', value: 'uploader' },
  { label: 'State', value: 'state' }
];

const DIRECTION_OPTIONS = [
  { label: 'Descending', value: 'desc' },
  { label: 'Ascending', value: 'asc' }
];

// Values must stay <= the controller's MAX_PAGE_SIZE, or it 400s.
const PAGE_SIZE_OPTIONS = [25, 50, 100].map((n) => ({
  label: `${n} per page`,
  value: String(n)
}));

const DEFAULT_PAGE_SIZE = 25;

const ALLOWED_ROLES: string[] = [Roles.Vcm, Roles.Superadmin];

type Props = {
  /**
   * Which queue to show. Pass this to let a parent own the tab state and render
   * its own tab strip; omit it for the standalone route.
   */
  tab?: ImageModerationTab;
  /** Which building tier to show. Omitted means paid. */
  tier?: ModerationTier;
};

const ImageModerationContainer = ({ tab: tabProp, tier = 'paid' }: Props) => {
  const { loader } = useFullScreenLoader();
  const userRole = useSelector((state: RootState) => state.auth.userRole);

  const [ownTab, setOwnTab] = useState<ImageModerationTab>('review');
  const tab = tabProp ?? ownTab;
  const setTab = setOwnTab;
  const [images, setImages] = useState<ModeratedImage[]>([]);
  const [thresholds, setThresholds] = useState<Thresholds>(DEFAULT_THRESHOLDS);
  const [apiState, setApiState] = useState<ApiState>('LOADING');

  // Outstanding rows per tab, for the tab badges. null = not counted yet.
  const [counts, setCounts] = useState<
    Record<ImageModerationTab, number | null>
  >({ hidden: null, review: null });

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [buildingsInQueue, setBuildingsInQueue] = useState<QueueBuilding[]>([]);

  const [building, setBuilding] = useState<string>('');
  const [category, setCategory] = useState<string>(ALL_CATEGORIES);
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [sortOption, setSortOption] = useState<string>('chronology');
  const [sortDirection, setSortDirection] = useState<string>('desc');

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [imageToEnlarge, setImageToEnlarge] = useState<ModeratedImage>();
  const [scanDetail, setScanDetail] = useState<ImageScanHistoryResponse>();

  const isAuthorized = ALLOWED_ROLES.includes(userRole);

  // Filtering, sorting and paging are all server-side: one page cannot sort or
  // filter rows it never received.
  // Responses can land out of order, so only the newest request may write state.
  const requestId = useRef(0);

  const fetchImages = useCallback(() => {
    const id = ++requestId.current;
    setApiState('LOADING');
    loader.show();

    return getImagesToModerate({
      tab,
      tier,
      page,
      pageSize,
      sort: sortOption,
      direction: sortDirection,
      ...(building && { building }),
      ...(category !== ALL_CATEGORIES && { category }),
      // End-of-day, or `to` would exclude everything after midnight that day.
      ...(from && { from: new Date(`${from}T00:00:00`).toISOString() }),
      ...(to && { to: new Date(`${to}T23:59:59.999`).toISOString() })
    })
      .then(({ data }) => {
        if (id !== requestId.current) return;
        setImages(data?.images || []);
        setTotal(data?.total ?? 0);
        setTotalPages(data?.totalPages ?? 0);
        setBuildingsInQueue(data?.buildings || []);
        if (data?.counts) {
          setCounts({ hidden: data.counts.hidden, review: data.counts.review });
        }
        if (data?.thresholds) setThresholds(data.thresholds);
      })
      .catch(() => {
        if (id === requestId.current) toast.error('Failed to load the image queue');
      })
      .finally(() => {
        // Newest request only. The loader is one shared boolean, not a counter,
        // so a stale response hiding it would clear the spinner while a newer
        // request is still running. Superseding always issues a newer request,
        // which hides on its own settle, so this cannot strand the spinner.
        if (id !== requestId.current) return;
        loader.hide();
        setApiState('COMPLETED');
      });
  }, [tab, tier, page, pageSize, building, category, from, to, sortOption, sortDirection]);

  useEffect(() => {
    if (isAuthorized) fetchImages();
  }, [fetchImages, isAuthorized]);

  // The page can outlive the rows under it — moderating the last row on the last
  // page, or a parent switching `tab` without going through changeFilter. Clamp
  // on the response so the pager cannot strand the user on an empty page.
  useEffect(() => {
    if (totalPages > 0 && page > totalPages - 1) setPage(totalPages - 1);
  }, [page, totalPages]);

  // `tier` is parent-owned, so it bypasses changeFilter. A building selected in
  // one tier is absent from the other's dropdown, emptying the queue.
  useEffect(() => {
    setPage(0);
    setSelectedIds([]);
    setBuilding('');
  }, [tier]);

  // Page 4 of the old filter may not exist under the new one. Reset here rather
  // than in an effect: an effect would let one fetch fire on the stale page
  // first, and whichever response landed last would win.
  const changeFilter = <T,>(set: (value: T) => void) => (value: T) => {
    setPage(0);
    setSelectedIds([]);
    set(value);
  };

  const buildingOptions = useMemo(
    () => [
      { label: 'All buildings', value: '' },
      ...buildingsInQueue.map((b) => ({
        label: b.buildingName,
        value: b.buildingId
      }))
    ],
    [buildingsInQueue]
  );

  // Already sorted, filtered and paged by the server.
  const visibleImages = images;

  const performAction = async (
    action: ImageModerationAction,
    ids: string[]
  ) => {
    if (!ids.length) return;

    loader.show();
    try {
      await performModerationActionOnImage({ id: ids, action });

      setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));

      toast.success(
        action === 'HIDE'
          ? `${ids.length} image(s) hidden`
          : action === 'UNHIDE'
          ? `${ids.length} image(s) cleared`
          : `${ids.length} image(s) marked reviewed`
      );

      // Awaited, and it owns the loader from here: the action's own hide() would
      // otherwise clear the spinner while this refetch is still in flight.
      // Refetch rather than splice — acting on a page shifts every later row up.
      await fetchImages();
    } catch (error) {
      toast.error('Action failed');
      loader.hide();
    }
  };

  const action = (type: ImageModerationAction, image: ModeratedImage) =>
    performAction(type, [image.uuid]);

  // Mark everything from the top of the list down to and including this row.
  const reviewAbove = (selected: ModeratedImage) => {
    const index = visibleImages.findIndex((img) => img.uuid === selected.uuid);
    if (index < 0) return;

    performAction(
      'MARK_REVIEWED',
      visibleImages.slice(0, index + 1).map((img) => img.uuid)
    );
  };

  const showDetails = async (image: ModeratedImage) => {
    loader.show();
    try {
      const res = await getImageScanHistory(image.uuid);
      setScanDetail(res.data);
    } catch (error) {
      toast.error('Failed to load scan history');
    } finally {
      loader.hide();
    }
  };

  const toggleSelect = (uuid: string) =>
    setSelectedIds((prev) =>
      prev.includes(uuid) ? prev.filter((id) => id !== uuid) : [...prev, uuid]
    );

  const columns = useMemo(
    () =>
      getColumnsForImageModerationTable({
        tab,
        thresholds,
        selectedIds,
        onToggleSelect: toggleSelect,
        onEnlarge: setImageToEnlarge,
        onShowDetails: showDetails,
        action,
        onReviewAboveClicked: reviewAbove
      }),
    [tab, thresholds, selectedIds, visibleImages]
  );

  function mouseEnterOnRow(row: ModeratedImage) {
    const el = document.getElementById(`reviewAboveButton-${row.uuid}`);
    if (el) el.style.visibility = 'visible';
  }

  function mouseLeftRow(row: ModeratedImage) {
    const el = document.getElementById(`reviewAboveButton-${row.uuid}`);
    if (el) el.style.visibility = 'hidden';
  }

  // Nested inside another screen's tab strip, which supplies its own chrome.
  const isEmbedded = tabProp !== undefined;

  // The server enforces this too (isVCMInAnyBuilding); this is for the message.
  if (!isAuthorized) {
    return (
      <div style={{ padding: isEmbedded ? 0 : '24px' }}>
        <h4>Image moderation is available to VCMs and Cobu admins only.</h4>
      </div>
    );
  }

  if (apiState === 'LOADING' && !images.length) return null;

  return (
    <div style={{ padding: isEmbedded ? 0 : '24px' }}>
      {!isEmbedded && (
        <div className='flex justify-start margin-bottom-16'>
          <Tabs.Container
            onChange={(value) =>
              value && changeFilter(setTab)(value as ImageModerationTab)
            }
            value={tab}
          >
            {TABS.map((option) => (
              <Tabs.Option
                key={option.value}
                value={option.value}
                selected={option.value === tab}
              >
                {counts[option.value] === null
                  ? option.label
                  : `${option.label} (${counts[option.value]})`}
              </Tabs.Option>
            ))}
          </Tabs.Container>
        </div>
      )}

      <div className={classes.filterBar}>
        <div className={classes.filterField}>
          <span className={classes.filterLabel}>Building</span>
          {/* CobuSelect is uncontrolled, so it remounts on key change. Length
              alone is not enough: two tabs can return the same count. */}
          <CobuSelect
            key={`building-${building}-${buildingOptions.length}`}
            options={buildingOptions}
            defaultValue={buildingOptions.find((o) => o.value === building)}
            onChange={(option: SelectedOptionType | null) =>
              changeFilter(setBuilding)(option?.value ?? '')
            }
            selectType={SelectType.Form}
            placeholder='All buildings'
          />
        </div>

        <div className={classes.filterField}>
          <span className={classes.filterLabel}>Category</span>
          <CobuSelect
            key={`category-${category}`}
            options={CATEGORY_OPTIONS}
            defaultValue={CATEGORY_OPTIONS.find((o) => o.value === category)}
            onChange={(option: SelectedOptionType | null) =>
              changeFilter(setCategory)(option?.value ?? ALL_CATEGORIES)
            }
            selectType={SelectType.Form}
            isSearchable={false}
            placeholder='Category'
          />
        </div>

        <div className={`${classes.filterField} ${classes.dateField}`}>
          <span className={classes.filterLabel}>From</span>
          <input
            type='date'
            value={from}
            max={to || undefined}
            onChange={(e) => changeFilter(setFrom)(e.target.value)}
          />
        </div>

        <div className={`${classes.filterField} ${classes.dateField}`}>
          <span className={classes.filterLabel}>To</span>
          <input
            type='date'
            value={to}
            min={from || undefined}
            onChange={(e) => changeFilter(setTo)(e.target.value)}
          />
        </div>

        <div className={classes.filterField}>
          <span className={classes.filterLabel}>Sort by</span>
          <CobuSelect
            options={SORT_OPTIONS}
            defaultValue={SORT_OPTIONS.find((o) => o.value === sortOption)}
            onChange={(option: SelectedOptionType | null) =>
              option && changeFilter(setSortOption)(option.value)
            }
            selectType={SelectType.Form}
            isSearchable={false}
            placeholder='Sort by'
          />
        </div>

        <div className={classes.filterField}>
          <span className={classes.filterLabel}>Order</span>
          <CobuSelect
            options={DIRECTION_OPTIONS}
            defaultValue={DIRECTION_OPTIONS.find(
              (o) => o.value === sortDirection
            )}
            onChange={(option: SelectedOptionType | null) =>
              option && changeFilter(setSortDirection)(option.value)
            }
            selectType={SelectType.Form}
            isSearchable={false}
            placeholder='Order'
          />
        </div>

        <div className={classes.filterField}>
          <span className={classes.filterLabel}>Show</span>
          <CobuSelect
            options={PAGE_SIZE_OPTIONS}
            defaultValue={PAGE_SIZE_OPTIONS.find(
              (o) => o.value === String(pageSize)
            )}
            onChange={(option: SelectedOptionType | null) =>
              option && changeFilter(setPageSize)(Number(option.value))
            }
            selectType={SelectType.Form}
            isSearchable={false}
            placeholder='Per page'
          />
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}
      >
        <h4 style={{ color: Colors.cobuTextBlack, margin: 0 }}>
          {tab === 'hidden' ? 'Withheld images' : 'Images needing review'} -{' '}
          {total}
        </h4>
        {tab === 'hidden' && (
          <span className={classes.mutedText}>
            Withheld by the model. UNHIDE releases one.
          </span>
        )}
      </div>

      {/* Bulk actions, for high-volume properties. */}
      {tab === 'review' && selectedIds.length > 0 && (
        <div className={classes.bulkBar}>
          <span>{selectedIds.length} selected</span>
          <button
            className={classes.redText}
            onClick={() => performAction('HIDE', selectedIds)}
          >
            HIDE SELECTED
          </button>
          <button onClick={() => performAction('MARK_REVIEWED', selectedIds)}>
            MARK REVIEWED
          </button>
          <button className='btn-ghost' onClick={() => setSelectedIds([])}>
            CLEAR SELECTION
          </button>
        </div>
      )}

      <CobuTableAdvanced
        rowProps={{
          onMouseEnter: mouseEnterOnRow,
          onMouseLeave: mouseLeftRow
        }}
        columns={columns}
        data={visibleImages}
        ignoreDefaultSortBehaviour
        noData={{ label: 'No images in this queue' }}
      />

      {totalPages > 1 && (
        <div className={classes.pager}>
          <button
            className='btn-ghost'
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            PREVIOUS
          </button>
          <span className={classes.mutedText}>
            Page {page + 1} of {totalPages}
          </span>
          <button
            className='btn-ghost'
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            NEXT
          </button>
        </div>
      )}

      <GenericModal
        title='Image'
        showModal={!!imageToEnlarge}
        hideActionButton
        cancelOverride='Close'
        content={
          imageToEnlarge ? (
            <img
              src={imageToEnlarge.image}
              alt='Moderated upload'
              style={{ maxWidth: '100%' }}
            />
          ) : null
        }
        onChange={() => setImageToEnlarge(undefined)}
        enableOverflowScrolls
      />

      <GenericModal
        title='Moderation detail'
        showModal={!!scanDetail}
        hideActionButton
        cancelOverride='Close'
        content={<ScanDetailModal data={scanDetail} />}
        onChange={() => setScanDetail(undefined)}
        enableOverflowScrolls
      />
    </div>
  );
};

/**
 * For embedding inside a screen that already has a FullScreenLoader provider —
 * wrapping again would stack a second loader overlay on top of the first.
 */
export const ImageModerationPanel = ImageModerationContainer;

export default withFullScreenLoader(ImageModerationContainer);
